import type { ChartConfigSeriesData, ChartDataInterface } from 'src/types/Chart';

type TariffMetadata = NonNullable<ChartConfigSeriesData['metaData']>[number];

const metadataByTariff = (metadata: ChartConfigSeriesData['metaData']): Map<string, TariffMetadata> => {
	const result = new Map<string, TariffMetadata>();
	(metadata || []).forEach((entry) => {
		if (!entry?.tariff) return;
		const key = String(entry.tariff.id || entry.tariff.name);
		const existing = result.get(key);
		if (!existing) {
			result.set(key, {
				...entry,
				tariff: { ...entry.tariff },
			});
			return;
		}
		existing.tariff.value += Number(entry.tariff.value || 0);
		existing.tariff.cost += Number(entry.tariff.cost || 0);
	});
	return result;
};

/**
 * Give every full-screen tooltip bucket the complete tariff schema. Missing future buckets keep
 * the same rows as measured buckets, but report truthful zero values instead of stale data.
 */
export const completeFullscreenTooltipData = (chartData: ChartDataInterface): ChartDataInterface => ({
	...chartData,
	series: chartData.series.map((series) => {
		const templates = new Map<string, TariffMetadata>();
		(series.data || []).forEach((point) => {
			metadataByTariff(point.metaData).forEach((entry, key) => {
				if (!templates.has(key)) templates.set(key, entry);
			});
		});

		if (!templates.size) return series;
		const currency = templates.values().next().value?.tariff.currency || '';

		return {
			...series,
			data: (series.data || []).map((point) => {
				const current = metadataByTariff(point.metaData);
				return {
					...point,
					cost: point.cost ?? 0,
					convertCost: point.convertCost || `0${currency ? ` ${currency}` : ''}`,
					metaData: Array.from(templates, ([key, template]) => {
						const measured = current.get(key);
						if (measured) return measured;
						return {
							...template,
							cost: `0${template.tariff.currency ? ` ${template.tariff.currency}` : ''}`,
							value: `0 ${template.tariff.unit}`.trim(),
							tariff: { ...template.tariff, cost: 0, value: 0 },
						};
					}),
				};
			}),
		};
	}),
});
